import * as actionTypes from "../actionTypes";
import * as api from "../../api/index";
import { toast } from 'react-toastify';
import { GetCurrenciesBIZ } from "./business/currenciesActions";

export const LogIn = (formData, navigate, setLoading, onMfaRequired) => async (dispatch) => {
  dispatch({ type: actionTypes.CLEAR_ERRORS });
  setLoading(true);
  try {
    const { data, status } = await api.login(formData);
    if (status === 200) {
      // If MFA is enabled, the backend returns a challenge token (no auth cookie
      // is set yet). The AuthPage handles the second step via VerifyMfaLogin.
      if (data?.data?.mfa_required) {
        if (typeof onMfaRequired === "function") {
          onMfaRequired(data.data);
        }
        setLoading(false);
        return;
      }
      // Otherwise: the backend already set the HttpOnly auth cookie + the CSRF
      // cookie via Set-Cookie headers. We also cache the CSRF token in memory
      // so we can echo it in the X-CSRF-Token header on subsequent requests —
      // cross-origin frontends can't read the CSRF cookie via document.cookie.
      api.setCsrfToken(data.data?.csrf_token);
      dispatch({ type: actionTypes.LOG_IN, payload: data.data?.user_info || null });
      await dispatch(GetCurrenciesBIZ(1, 500, "", setLoading));
      toast.success(data.message);
      navigate("/watch");
    } else {
      dispatch({ type: actionTypes.SET_ERRORS, payload: data.message });
      toast.error(data.message || "Login failed");
    }
    setLoading(false);
  } catch (error) {
    // api.login uses _skipAuthRedirect, so 401s land here as the backend error
    // payload (not the global "Session expired" flow). Show whichever field
    // the backend populated — usually `message`.
    toast.error(error?.message || "Login failed. Please try again.");
    dispatch({ type: actionTypes.SET_ERRORS, payload: error?.message || "Login failed" });
    setLoading(false);
  }
};

export const VerifyMfaLogin = (formData, navigate, setLoading) => async (dispatch) => {
  dispatch({ type: actionTypes.CLEAR_ERRORS });
  setLoading(true);
  try {
    const { data, status } = await api.verifyMfaLogin(formData);
    if (status === 200) {
      api.setCsrfToken(data.data?.csrf_token);
      dispatch({ type: actionTypes.LOG_IN, payload: data.data?.user_info || null });
      await dispatch(GetCurrenciesBIZ(1, 500, "", setLoading));
      toast.success(data.message);
      navigate("/watch");
    } else {
      dispatch({ type: actionTypes.SET_ERRORS, payload: data.message });
      toast.error(data.message || "Verification failed");
    }
    setLoading(false);
  } catch (error) {
    // Same rationale as LogIn — surface the real error, not "Session expired".
    toast.error(error?.message || "Invalid code, please try again.");
    dispatch({ type: actionTypes.SET_ERRORS, payload: error?.message || "Verification failed" });
    setLoading(false);
  }
};

// LogOut hits the backend so it can clear the HttpOnly cookies; the local
// LOG_OUT dispatch fires regardless so the UI updates even if the network
// call fails (e.g. cookie is already gone).
export const LogOut = (navigate) => async (dispatch) => {
  try {
    await api.logout();
  } catch (error) {
    // ignore — we still want to clear local state
  }
  api.setCsrfToken(null);
  dispatch({ type: actionTypes.LOG_OUT });
  if (typeof navigate === "function") {
    navigate("/");
  }
};

// Called on app load by ProtectedRoutes. Hits a server endpoint that only
// returns 200 when the HttpOnly auth cookie is present and valid. If 401,
// we know the user is signed out — the special _skipAuthRedirect flag on
// this request prevents the global 401 handler from triggering a toast.
// The /me response also carries the current CSRF token so we can re-populate
// our in-memory store after a hard reload (when the store starts empty).
export const BootstrapAuth = () => async (dispatch) => {
  try {
    const { data, status } = await api.bootstrapAuthCheck();
    if (status === 200) {
      api.setCsrfToken(data.data?.csrf_token);
      dispatch({ type: actionTypes.BOOTSTRAP_AUTH_OK, payload: data.data || null });
      return true;
    }
    dispatch({ type: actionTypes.BOOTSTRAP_AUTH_FAIL });
    return false;
  } catch (error) {
    dispatch({ type: actionTypes.BOOTSTRAP_AUTH_FAIL });
    return false;
  }
};
